import React, { useState, useEffect } from "react";
import "./settingsLocations.css";
import SearchIcon from "./assets/icon-search.png";
import OptionsIcon from "./assets/optionsIcon.png";
import { useSelector } from "react-redux";
import { authenticatedFetch } from "../../../network/api";

import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";

import AddLocationModal from "../../addLawyer/addLocationModal";
import AddVideoLocationModal from "../../addLawyer/addVideoLocationModal";

const SettingsLocations = () => {
  // Core list & filter state
  const [searchName, setSearchName] = useState("");
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [selectedType, setSelectedType] = useState(""); // "In Person" / "Video"
  const [isLoading, setIsLoading] = useState(true);
  const [inviteError, setInviteError] = useState(null);

  // Context menu for each row
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const handleMenuClick = (e, row) => {
    setCurrentRow(row);
    setAnchorEl(e.currentTarget);
  };
  const handleCloseMenu = () => setAnchorEl(null);

  // "Add" dropdown state
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const handleAddMenuOpen = (e) => setAddMenuAnchor(e.currentTarget);
  const handleAddMenuClose = () => setAddMenuAnchor(null);

  // Modal open state
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  // Selection & lists for onboarding logic
  const [inPerson, setInPerson] = useState(false);
  const [videoVisit, setVideoVisit] = useState(false);
  const [selectedLoc, setSelectedLoc] = useState({});
  const [selectedVideoLoc, setSelectedVideoLoc] = useState({});

  const firmId = useSelector((s) => s.firm.details?.id);

  // Fetch all existing locations on mount
  const fetchLocations = async () => {
    try {
      const res = await authenticatedFetch(
        `/api/attorney/webapp/fetchFirmLocationsSetting?firmId=${firmId}`
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "Failed to load");
      setLocations(body.locations);
      setFilteredLocations(body.locations);
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (firmId) fetchLocations();
  }, [firmId]);

  // Filter list by type
  const filterByType = (type) => {
    const next = selectedType === type ? "" : type;
    setSelectedType(next);
    setFilteredLocations(
      next ? locations.filter((l) => l.locationType === next) : locations
    );
  };

  // Onboarding toggles fetch subset lists
  const handleInPersonChange = async (checked) => {
    setInPerson(checked);
    if (!checked) return setLocations([]);
    const res = await authenticatedFetch(
      `/api/attorney/webapp/fetchFirmLocations?firmId=${firmId}&method=person`
    );
    const data = await res.json();
    setLocations(Array.isArray(data) ? data : data.locations || []);
    setFilteredLocations(Array.isArray(data) ? data : data.locations || []);
  };
  const handleVideoChange = async (checked) => {
    setVideoVisit(checked);
    if (!checked) return setLocations([]);
    const res = await authenticatedFetch(
      `/api/attorney/webapp/fetchFirmLocations?firmId=${firmId}&method=video`
    );
    const data = await res.json();
    setLocations(Array.isArray(data) ? data : data.locations || []);
    setFilteredLocations(Array.isArray(data) ? data : data.locations || []);
  };

  // Handlers to open modals
  const openLocationModal = () => setIsLocationModalOpen(true);
  const closeLocationModal = () => setIsLocationModalOpen(false);
  const openVideoLocationModal = () => setIsVideoModalOpen(true);
  const closeVideoLocationModal = () => setIsVideoModalOpen(false);

  // Callbacks from modals
  const handleAddLocation = async (newLocation) => {
    try {
      const res = await authenticatedFetch(`/api/attorney/webapp/locations/addInPerson`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firmId,
          name: newLocation.name,
          address: newLocation.address,
          city: newLocation.city,
          state: newLocation.state,
          zip: newLocation.zip,
          latitude: newLocation.latitude,
          longitude: newLocation.longitude,
          contactName: newLocation.contactName,
          notifEmail: newLocation.notifEmail,
          notifPhone: newLocation.notifPhone,
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "Failed to add location");
      setIsLoading(true);
      await fetchLocations();
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setIsLocationModalOpen(false);
    }
  };
  const handleAddVideoLocation = async (newLocation) => {
    try {
      const res = await authenticatedFetch(`/api/attorney/webapp/locations/addVideo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firmId,
          name: newLocation.name,
          state: newLocation.state,
          platform: newLocation.platform,
          contactName: newLocation.contactName,
          notifEmail: newLocation.notifEmail,
          notifPhone: newLocation.notifPhone,
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "Failed to add video location");
      setIsLoading(true);
      await fetchLocations();
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setIsVideoModalOpen(false);
    }
  };

  const handleDeleteCurrent = async () => {
    if (!currentRow) return;
    try {
      const type = currentRow.locationType === "In Person" ? "person" : "video";
      const url = `/api/attorney/webapp/locations/delete?id=${encodeURIComponent(
        currentRow.id
      )}&type=${type}&firmId=${encodeURIComponent(firmId)}`;
      const res = await authenticatedFetch(url, { method: "DELETE" });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || "Failed to delete");
      setIsLoading(true);
      await fetchLocations();
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setCurrentRow(null);
      handleCloseMenu();
    }
  };

  const formatDate = (ts) =>
    ts
      ? new Date(ts).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : "N/A";

  const menuStyles = {
    paper: { borderRadius: "8px", minWidth: "200px" },
    item: {
      padding: "12px 16px",
      fontFamily: "Poppins",
      fontWeight: 500,
      fontSize: "14px",
      lineHeight: "100%",
      letterSpacing: "-0.17px",
      color: "#333333",
    },
  };

  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: "130px" }}
    >
      <div className="locations-page-container">
        <div className="locations-header">
          <h1 className="locations-page-title">Locations</h1>

          {/* Add button with dropdown */}
          <button onClick={handleAddMenuOpen} className="btn-add">
            Add
          </button>
          <Menu
            anchorEl={addMenuAnchor}
            open={Boolean(addMenuAnchor)}
            onClose={handleAddMenuClose}
            PaperProps={{ style: { borderRadius: 8, minWidth: 180 } }}
          >
            <MenuItem
              onClick={() => {
                handleAddMenuClose();
                openLocationModal();
              }}
              sx={{ fontFamily: "Poppins" }}
            >
              In Person
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleAddMenuClose();
                openVideoLocationModal();
              }}
              sx={{ fontFamily: "Poppins" }}
            >
              Video Location
            </MenuItem>
          </Menu>
        </div>

        {/* Search & filter */}
        <div className="locations-search-div">
          <div className="locations-search__field">
            <img src={SearchIcon} alt="" className="locations__search__icon" />
            <input
              type="text"
              placeholder="Search by name..."
              className="locations_search__input"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
          </div>
          <button
            className="locations-filter-buttons"
            onClick={() => filterByType("In Person")}
          >
            In Person
          </button>
          <button
            className="locations-filter-buttons"
            onClick={() => filterByType("Video")}
          >
            Video
          </button>
        </div>

        {/* Modals */}
        <AddLocationModal
          isOpen={isLocationModalOpen}
          onClose={closeLocationModal}
          onAddLocation={handleAddLocation}
        />
        <AddVideoLocationModal
          isOpen={isVideoModalOpen}
          onClose={closeVideoLocationModal}
          onAddLocation={handleAddVideoLocation}
        />

        {inviteError && <div className="error-message">{inviteError}</div>}

        {/* Locations list */}
        {!isLoading && filteredLocations.length > 0 ? (
          <div className="locations-list">
            <div className="locations-table-header">
              <p className="locations-table-titles">Location</p>
              <p className="locations-table-titles">Type</p>
              <p className="locations-table-titles">Contact</p>
              <p className="locations-table-titles">Phone</p>
              <p className="locations-table-titles">Date Added</p>
            </div>

            {filteredLocations
              .filter((l) =>
                (l.locationName || "")
                  .toLowerCase()
                  .includes(searchName.toLowerCase())
              )
              .map((loc) => (
                <div key={loc.id} className="locations-table-row">
                  <div className="locations-table-content">
                    {loc.locationName}
                  </div>
                  <div className="locations-table-content">
                    {loc.locationType}
                  </div>
                  <div className="locations-table-content">
                    {loc.contactName}
                  </div>
                  <div className="locations-table-content">
                    {loc.contactPhone}
                  </div>
                  <div className="locations-table-content">
                    {formatDate(loc.created_at)}
                  </div>
                  <div style={{display: "flex", justifyContent: "flex-end"}}>
                    <IconButton onClick={(e) => handleMenuClick(e, loc)}>
                      <img
                        src={OptionsIcon}
                        alt="options"
                        style={{ width: 24, height: 24 }}
                      />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleCloseMenu}
                      PaperProps={{ style: menuStyles.paper }}
                    >
                      <MenuItem style={menuStyles.item}>Edit</MenuItem>
                      <MenuItem style={menuStyles.item} onClick={handleDeleteCurrent}>Delete</MenuItem>
                    </Menu>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <p className="empty-message">
            No locations found{isLoading ? "…" : ""}.
          </p>
        )}
      </div>
    </div>
  );
};

export default SettingsLocations;
